<template>
  <div
    v-click-outside="{
      handler: close,
      closeConditional,
      include: include,
    }"
  >
    <v-navigation-drawer
      :key="`table-create-drawer-${effectiveTableNumber}`"
      ref="drawer"
      :value="isVisible"
      app
      right
      temporary
      stateless
      width="400"
      class="table-create-drawer"
    >
      <!-- NOTE v-app-barのv-ifは設定しないとHall.vueのタブ移動でなぜか一瞬表示されてしまう問題対策 -->
      <v-app-bar
        v-if="isVisible"
        class="mb-4"
        outlined
        fixed
        flat
        style="z-index: 10"
      >
        <v-app-bar-nav-icon>
          <v-btn
            fab
            small
            :ripple="false"
            @click="close"
          >
            <v-icon>
              {{ icons.mdiDotsGrid }}
            </v-icon>
          </v-btn>
        </v-app-bar-nav-icon>

        <!-- 卓選択モード: hallsが渡された場合はv-selectで空き卓を選べる -->
        <v-list-item v-if="isTableSelectMode">
          <v-list-item-content>
            <v-list-item-title class="d-flex">
              <div @click.stop>
                <v-select
                  v-model="selectedTable"
                  :items="movableTables"
                  :prepend-icon="icons.mdiArrowRightBold"
                  dense
                  required
                  :error="!selectedTable"
                  :error-messages="!selectedTable ? [`利用可能な${tableSuffix}を選択してください`] : []"
                  @change="onTableSelect"
                />
              </div>
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>

        <!-- 通常モード: 固定の卓名を表示 -->
        <v-app-bar-title v-else>
          <template #default>
            <table-name
              :table-number="tableNumber"
              :table-prefix="tablePrefix"
              :table-suffix="tableSuffix"
            />
          </template>
        </v-app-bar-title>
      </v-app-bar>

      <v-card
        elevation="0"
        tile
      >
        <v-card-text class="pb-16">
          <v-card
            elevation="0"
            class="mb-7 mt-10"
          >
            <v-card-subtitle
              class="text-sm pa-0 pt-3 mb-5"
            >
              客数
            </v-card-subtitle>
            <div
              class="d-flex align-center flex-wrap px-1 customer-counter-input-wrapper"
            >
              <number-counter
                v-if="isVisible"
                :number="customerCount"
                :min="1"
                :open-by-default="isVisible"
                class="customer-counter-input"
                @change="customerCount = $event"
              >
                <template #prepend-number>
                  <v-icon>
                    {{ icons.mdiAccountOutline }}
                  </v-icon>
                </template>
              </number-counter>
            </div>

            <customer-table-tagging
              @updated="setCustomerTableTaggings"
            />

            <customer-select
              ref="customerSelect"
              v-model="selectedCustomerIds"
              :initial-customer-ids="initialCustomerIds"
              class="mt-4"
              @change="setSelectedCustomerIds"
            />
          </v-card>

          <!-- リファラル start -->
          <v-card
            v-if="effectiveReferrals.length > 0 && users.length > 0"
            elevation="0"
          >
            <v-card-subtitle
              class="text-sm pa-0 d-flex justify-space-between align-center pb-2 referral-subtitle"
            >
              {{ currentClub.referralAlias }}
              <v-btn
                :ripple="false"
                icon
                color="primary"
                class="referral-add-btn"
                @click="addReferringHistoryTemp({})"
              >
                <v-icon>
                  {{ icons.mdiPlus }}
                </v-icon>
              </v-btn>
            </v-card-subtitle>

            <!-- eslint-disable vue/v-on-event-hyphenation -->
            <transition-group
              name="fadeUp"
              class="d-flex"
              :class="$vuetify.breakpoint.xsOnly ? 'flex-column' : 'flex-column-reverse'"
            >
              <div
                v-for="(referringHistoryTemp, referringHistoryTempIdx) in referringHistoriesTemp"
                :key="`referringHistoriesTemp[${referringHistoryTempIdx}]`"
                style="animation-duration: 0.3s"
              >
                <referral-card
                  :id="`referral-card-${referringHistoryTempIdx}`"
                  :key="`referral-card-${referringHistoryTempIdx}/${referringHistoriesTemp.length}`"
                  :value="referringHistoryTemp"
                  :referrals="effectiveReferrals"
                  :users="users"
                  :referring-history-temp-idx="referringHistoryTempIdx"
                  class="mt-3 mb-8"
                  @update:referralId="referringHistoryTemp.referralId = $event"
                  @update:userId="referringHistoryTemp.userId = $event"
                  @remove="removeReferringHistoryTemp($event)"
                />
              </div>
            </transition-group>
          </v-card>
          <!-- リファラル end -->

          <!-- ノミネーション start -->
          <v-card
            v-if="effectiveNominations.length > 0 && users.length > 0"
            elevation="0"
            class="my-10 nomination-order-cards"
          >
            <v-card-subtitle
              class="text-sm pa-0 d-flex justify-space-between align-center pb-2 nomination-subtitle"
            >
              {{ currentClub.nominationAlias }}
              <v-btn
                icon
                :ripple="false"
                color="primary"
                class="nomination-add-btn"
                @click="addNominationWithCustomer()"
              >
                <v-icon>
                  {{ icons.mdiPlus }}
                </v-icon>
              </v-btn>
            </v-card-subtitle>

            <!-- eslint-disable vue/v-on-event-hyphenation -->
            <transition-group
              name="fadeUp"
              class="d-flex"
              :class="$vuetify.breakpoint.xsOnly ? 'flex-column' : 'flex-column-reverse'"
            >
              <div
                v-for="(nominationHistoryTemp, nominationHistoryTempIdx) in nominationHistoriesTemp"
                :key="`nominationHistoriesTemp[${nominationHistoryTempIdx}]`"
                style="animation-duration: 0.3s"
              >
                <order-card
                  :id="`nomination-card-${nominationHistoryTempIdx}`"
                  :key="`nomination-order-card-${nominationHistoryTempIdx}/${nominationHistoriesTemp.length}`"
                  v-model="nominationHistoryTemp.value"
                  :vendibles="effectiveNominations"
                  :users="users"
                  :size="nominationHistoryTemp.size"
                  class="mt-3 mb-8"
                  @update:vendibleId="nominationHistoryTemp.value.vendibleId = $event"
                  @update:vendibleType="nominationHistoryTemp.value.vendibleType = $event"
                  @update:sellingPrice="nominationHistoryTemp.value.sellingPrice = $event"
                  @update:taxCharge="nominationHistoryTemp.value.taxCharge = $event"
                  @update:vendingContributionsAttributes="nominationHistoryTemp.value.vendingContributionsAttributes = $event"
                  @update:isArbitraryVendingContribution="nominationHistoryTemp.isArbitraryVendingContribution = $event"
                  @update:vendibleRecipientsAttributes="nominationHistoryTemp.value.vendibleRecipientsAttributes = $event"
                  @update:vendingHistoryCustomersAttributes="nominationHistoryTemp.value.vendingHistoryCustomersAttributes = $event"
                  @update:size="nominationHistoryTemp.size = $event"
                  @remove="removeVendingHistoryTempByValue(nominationHistoryTemp.value)"
                />
              </div>
            </transition-group>
          </v-card>
          <!-- ノミネーション end -->

          <!-- コース start -->
          <v-card
            v-if="effectiveCourses.length > 0"
            elevation="0"
            class="my-10 course-order-cards"
          >
            <v-card-subtitle
              class="text-sm pa-0 d-flex justify-space-between align-center pb-2 course-subtitle"
            >
              {{ currentClub.courseAlias }}
              <v-btn
                icon
                :ripple="false"
                color="primary"
                class="course-add-btn"
                @click="addVendingHistoryTemp({ vendibleType: 'Course' })"
              >
                <v-icon>
                  {{ icons.mdiPlus }}
                </v-icon>
              </v-btn>
            </v-card-subtitle>

            <!-- eslint-disable vue/v-on-event-hyphenation -->
            <transition-group
              name="fadeUp"
              class="d-flex"
              :class="$vuetify.breakpoint.xsOnly ? 'flex-column' : 'flex-column-reverse'"
            >
              <div
                v-for="(courseHistoryTemp, courseHistoryTempIdx) in courseHistoriesTemp"
                :key="`courseHistoriesTemp[${courseHistoryTempIdx}]`"
                style="animation-duration: 0.3s"
              >
                <order-card
                  :id="`course-card-${courseHistoryTempIdx}`"
                  :key="`course-order-card-${courseHistoryTempIdx}/${courseHistoriesTemp.length}`"
                  v-model="courseHistoryTemp.value"
                  :vendibles="effectiveCourses"
                  :users="users"
                  :size="courseHistoryTemp.size"
                  class="mt-3 mb-8"
                  @update:vendibleId="courseHistoryTemp.value.vendibleId = $event"
                  @update:vendibleType="courseHistoryTemp.value.vendibleType = $event"
                  @update:sellingPrice="courseHistoryTemp.value.sellingPrice = $event"
                  @update:taxCharge="courseHistoryTemp.value.taxCharge = $event"
                  @update:vendingContributionsAttributes="courseHistoryTemp.value.vendingContributionsAttributes = $event"
                  @update:isArbitraryVendingContribution="courseHistoryTemp.isArbitraryVendingContribution = $event"
                  @update:vendingHistoryCustomersAttributes="courseHistoryTemp.value.vendingHistoryCustomersAttributes = $event"
                  @update:size="courseHistoryTemp.size = $event"
                  @remove="removeVendingHistoryTempByValue(courseHistoryTemp.value)"
                />
              </div>
            </transition-group>
          </v-card>
          <!-- コース end -->

          <!-- Item start -->
          <v-card
            v-if="effectiveItems.length > 0"
            elevation="0"
            class="my-10"
          >
            <v-card-subtitle
              class="text-sm pa-0 d-flex justify-space-between align-center pb-2"
            >
              チャージ
              <v-btn
                icon
                :ripple="false"
                color="primary"
                @click="addVendingHistoryTemp({ vendibleType: 'Item' })"
              >
                <v-icon>
                  {{ icons.mdiPlus }}
                </v-icon>
              </v-btn>
            </v-card-subtitle>

            <!-- eslint-disable vue/v-on-event-hyphenation -->
            <transition-group
              name="fadeUp"
              class="d-flex"
              :class="$vuetify.breakpoint.xsOnly ? 'flex-column' : 'flex-column-reverse'"
            >
              <div
                v-for="(itemHistoryTemp, itemHistoryTempIdx) in itemHistoriesTemp"
                :key="`itemHistoriesTemp[${itemHistoryTempIdx}]`"
                style="animation-duration: 0.3s"
              >
                <order-card
                  :key="`item-order-card-${itemHistoryTempIdx}/${itemHistoriesTemp.length}`"
                  v-model="itemHistoryTemp.value"
                  :vendibles="effectiveItems"
                  :users="users"
                  :size="itemHistoryTemp.size"
                  class="mt-3 mb-8"
                  @update:vendibleId="itemHistoryTemp.value.vendibleId = $event"
                  @update:vendibleType="itemHistoryTemp.value.vendibleType = $event"
                  @update:sellingPrice="itemHistoryTemp.value.sellingPrice = $event"
                  @update:taxCharge="itemHistoryTemp.value.taxCharge = $event"
                  @update:vendingContributionsAttributes="itemHistoryTemp.value.vendingContributionsAttributes = $event"
                  @update:isArbitraryVendingContribution="itemHistoryTemp.isArbitraryVendingContribution = $event"
                  @update:vendibleRecipientsAttributes="itemHistoryTemp.value.vendibleRecipientsAttributes = $event"
                  @update:vendingHistoryCustomersAttributes="itemHistoryTemp.value.vendingHistoryCustomersAttributes = $event"
                  @update:size="itemHistoryTemp.size = $event"
                  @remove="removeVendingHistoryTempByValue(itemHistoryTemp.value)"
                />
              </div>
            </transition-group>
          </v-card>
          <!-- Item end -->
        </v-card-text>
      </v-card>
      <v-app-bar
        bottom
        outlined
        height="76"
        fixed
      >
        <div class="d-flex flex-column align-center w-full h-full px-5">
          <v-spacer />
          <v-btn
            color="primary"
            block
            :ripple="false"
            :loading="isCreating"
            :disabled="!canCreate || isCreating"
            @click="createTable"
          >
            作成
          </v-btn>
          <v-spacer />
        </div>
      </v-app-bar>
    </v-navigation-drawer>
  </div>
</template>

<script>
import {
  ref,
  computed,
  watch,
  inject,
  provide,
  getCurrentInstance,
} from '@vue/composition-api'
import {
  mdiDotsGrid,
  mdiPlus,
  mdiViewGridPlus,
  mdiCashMarker,
  mdiAccountOutline,
  mdiAccountCircleOutline,
  mdiArrowRightBold,
} from '@mdi/js'
import { cloneDeep } from 'lodash'
import useVendingHistory from '@/views/composable/useVendingHistory'
import useReferral from '@/views/composable/useReferral'
import usePreventBackgroundScrolling from '@/views/composable/usePreventBackgroundScrolling'
import TableApi from '@/api/waiter/Table'
import TableName from '@/views/components/table/TableName.vue'
import NumberCounter from '@/views/components/util/NumberCounter.vue'
import ReferralCard from '@/views/components/table/ReferralCard.vue'
import OrderCard from '@/views/components/table/OrderCard/OrderCard.vue'
import CustomerTableTagging from './CustomerTableTagging.vue'
import CustomerSelect from './CustomerSelect.vue'

export default {
  components: {
    CustomerTableTagging,
    CustomerSelect,
    TableName,
    NumberCounter,
    ReferralCard,
    OrderCard,
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    hallId: {
      type: [Number, String],
      default: null,
    },
    tableNumber: {
      type: [Number, String],
      default: null,
    },
    tablePrefix: {
      type: String,
      default: '',
    },
    tableSuffix: {
      type: String,
      default: '卓',
    },
    referrals: {
      type: Array,
      default: () => [],
    },
    nominations: {
      type: Array,
      default: () => [],
    },
    courses: {
      type: Array,
      default: () => [],
    },
    items: {
      type: Array,
      default: () => [],
    },
    initialCustomerIds: {
      type: Array,
      default: () => [],
    },
    // 卓選択モード用: halls一覧を渡すと、ヘッダーで空き卓を選べるようになる
    halls: {
      type: Array,
      default: () => [],
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance().proxy

    const customerCount = ref(1)
    const isCreating = ref(false)
    const selectedCustomerIds = ref([...props.initialCustomerIds])
    const customerSelect = ref(null)

    const users = inject('users', [])

    // --- 卓選択モード ---
    const isTableSelectMode = computed(() => props.halls.length > 0)
    const selectedTable = ref(null)

    const sortedHalls = computed(() => {
      return props.halls.slice().sort((a, b) => {
        return a.attributes.position - b.attributes.position
      })
    })

    // 出戻りドロワーと同じパターン: 埋まっている卓を除外した選択肢を生成
    const movableTables = computed(() => {
      return sortedHalls.value.flatMap(hall => {
        const ongoingTableNumbers = hall.attributes.tables.data.map(table => table.attributes.tableNumber)

        const tables = Array.from({ length: hall.attributes.tableCount }, (_, i) => {
          const tn = i + 1
          if (ongoingTableNumbers.includes(tn)) return null

          return {
            text: [hall.attributes.tablePrefix, tn, hall.attributes.tableSuffix].join(''),
            value: `${hall.id}-${tn}`,
          }
        }).filter(Boolean)

        return [{ header: hall.attributes.name }, ...tables]
      })
    })

    // 選択されたホールのtableInitConfigからvendibleデータを取得
    const selectedHall = computed(() => {
      if (!selectedTable.value) return null
      const hallId = selectedTable.value.split('-')[0]

      return props.halls.find(h => String(h.id) === String(hallId)) || null
    })

    const selectedHallConfig = computed(() => {
      return selectedHall.value?.attributes?.tableInitConfig?.data?.attributes || null
    })

    // effective: 卓選択モード時はselectedHallConfigから取得、通常はpropsから取得
    const effectiveHallId = computed(() => {
      if (isTableSelectMode.value && selectedTable.value) {
        return Number(selectedTable.value.split('-')[0])
      }

      return props.hallId
    })

    const effectiveTableNumber = computed(() => {
      if (isTableSelectMode.value && selectedTable.value) {
        return Number(selectedTable.value.split('-')[1])
      }

      return props.tableNumber
    })

    const effectiveReferrals = computed(() => {
      if (isTableSelectMode.value) {
        return selectedHallConfig.value?.referrals?.data || []
      }

      return props.referrals
    })

    const effectiveNominations = computed(() => {
      if (isTableSelectMode.value) {
        return selectedHallConfig.value?.nominations?.data || []
      }

      return props.nominations
    })

    const effectiveCourses = computed(() => {
      if (isTableSelectMode.value) {
        return selectedHallConfig.value?.courses?.data || []
      }

      return props.courses
    })

    const effectiveItems = computed(() => {
      if (isTableSelectMode.value) {
        return selectedHallConfig.value?.items?.data || []
      }

      return props.items
    })

    // --- composables ---
    const {
      referringHistoriesTemp,
      addReferringHistoryTemp,
      removeReferringHistoryTemp,
      updateReferringHistoryTemp,
      resetReferringHistoriesTemp,
      isValidToSubmit: isValidReferringHistoriesToSubmit,
    } = useReferral()

    // NOTE: useVendingHistoryにreferringHistoriesTempをinjectするため
    // provideしてからuseVendingHistory()しないとinjectされない（順番に依存している）
    provide('referringHistoriesTemp', referringHistoriesTemp)
    const {
      vendingHistoriesTemp,
      nominationHistoriesTemp,
      courseHistoriesTemp,
      itemHistoriesTemp,
      vendingHistoriesToSubmit,
      addVendingHistoryTemp,
      removeVendingHistoryTempByValue,
      resetVendingHistoriesTemp,
      isValidToSubmit: isValidVendingHistoriesToSubmit,
      setVendingContributionsAttributes,
    } = useVendingHistory()

    const customerTableTaggings = ref([])

    const { target: drawer } = usePreventBackgroundScrolling()

    const isValidContents = computed(() => {
      return isValidReferringHistoriesToSubmit.value && isValidVendingHistoriesToSubmit.value
    })

    // 卓選択モード時は卓が選択されていることも必要
    const canCreate = computed(() => {
      if (isTableSelectMode.value) {
        return isValidContents.value && !!selectedTable.value
      }

      return isValidContents.value
    })

    const setCustomerTableTaggings = tags => {
      customerTableTaggings.value = tags
    }

    const setSelectedCustomerIds = ids => {
      selectedCustomerIds.value = ids
    }

    const addNominationWithCustomer = () => {
      const vendingHistoryCustomersAttributes = selectedCustomerIds.value.length === 1
        ? [{ customerId: selectedCustomerIds.value[0] }]
        : []

      addVendingHistoryTemp({
        vendibleId: effectiveNominations.value[0].id,
        vendibleType: 'Nomination',
        sellingPrice: effectiveNominations.value[0].attributes.sellingPrice,
        vendingHistoryCustomersAttributes,
      })
    }

    // 卓選択変更時にvendingHistoriesをリセットして初期コースを追加
    const onTableSelect = () => {
      resetVendingHistoriesTemp()

      if (effectiveCourses.value.length > 0 && effectiveCourses.value[0]?.id) {
        addVendingHistoryTemp({
          vendibleId: effectiveCourses.value[0].id,
          vendibleType: 'Course',
          sellingPrice: effectiveCourses.value[0].attributes.sellingPrice,
        })
      }
    }

    // NOTE: ドロワーが開かれた時に
    // セット付きの卓であればcourseHistoryTempをつくる
    // 初期顧客IDがあればセットする
    watch(
      () => props.isVisible,
      (newVal, _) => {
        if (!newVal) return

        if (props.initialCustomerIds.length > 0 && selectedCustomerIds.value.length === 0) {
          selectedCustomerIds.value = [...props.initialCustomerIds]
        }

        // 通常モード時のみ初期コースを追加（卓選択モードはonTableSelectで処理）
        if (!isTableSelectMode.value && courseHistoriesTemp.value.length < 1 && props.courses[0]?.id) {
          addVendingHistoryTemp({
            vendibleId: props.courses[0].id,
            vendibleType: 'Course',
            sellingPrice: props.courses[0].attributes.sellingPrice,
          })
        }
      },
      { immediate: true },
    )

    // NOTE: courseHistoryTempが一個だけの時はsizeは客数と一致させる
    watch(
      [
        () => customerCount.value,
        () => courseHistoriesTemp.value.length,
      ],
      (
        [newCount, newLen],
        [_prevCount, _prevLen],
      ) => {
        if (newLen !== 1) return

        // eslint-disable-next-line no-param-reassign
        courseHistoriesTemp.value[0].size = newCount
      },
    )

    const reset = () => {
      customerCount.value = 1
      selectedCustomerIds.value = []
      selectedTable.value = null
      if (customerSelect.value?.reset) {
        customerSelect.value.reset()
      }
      resetReferringHistoriesTemp()
      resetVendingHistoriesTemp()
    }

    const close = () => {
      reset()
      emit('close')
    }

    const createTable = async () => {
      isCreating.value = true

      if (canCreate.value) {
        const hallId = effectiveHallId.value
        const tn = effectiveTableNumber.value
        const hall = isTableSelectMode.value ? selectedHall.value : null
        const prefix = isTableSelectMode.value ? (hall?.attributes?.tablePrefix || '') : props.tablePrefix
        const suffix = isTableSelectMode.value ? (hall?.attributes?.tableSuffix || '卓') : props.tableSuffix

        const res = await TableApi.createTable({
          hallId,
          tableNumber: tn,
          customerCount: customerCount.value,
          referringHistories: referringHistoriesTemp.value,
          vendingHistories: vendingHistoriesToSubmit.value,
          customerTableTaggings: customerTableTaggings.value,
          customerIds: selectedCustomerIds.value,
        })

        if (res?.data?.data) emit('created', res.data.data)

        vm.$toast.success(`${[prefix, tn, suffix].join('')}を作成しました`)

        close()
      }
      isCreating.value = false
    }

    // NOTE: 販売貢献人をvendingHistoryTempに自動セットするwatch
    watch(
      [
        () => cloneDeep(vendingHistoriesTemp.value),
        () => cloneDeep(referringHistoriesTemp.value),
      ],
      (
        [_new1, _new2],
        [_prev1, _prev2],
      ) => {
        setVendingContributionsAttributes({
          targetVendingHistoriesTemp: vendingHistoriesTemp.value,
          targetReferringHistoriesTemp: referringHistoriesTemp.value,
        })
      },
    )

    return {
      // data
      users,
      isCreating,
      customerCount,
      referringHistoriesTemp,
      customerTableTaggings,
      selectedCustomerIds,
      customerSelect,
      drawer,

      // 卓選択モード
      isTableSelectMode,
      selectedTable,
      movableTables,

      // computed
      effectiveTableNumber,
      effectiveReferrals,
      effectiveNominations,
      effectiveCourses,
      effectiveItems,
      nominationHistoriesTemp,
      courseHistoriesTemp,
      itemHistoriesTemp,
      isValidContents,
      canCreate,
      isValidReferringHistoriesToSubmit,
      isValidVendingHistoriesToSubmit,

      // methods
      addReferringHistoryTemp,
      removeReferringHistoryTemp,
      updateReferringHistoryTemp,
      addVendingHistoryTemp,
      removeVendingHistoryTempByValue,
      createTable,
      close,
      onTableSelect,
      closeConditional: () => !!document.querySelector('.v-overlay--active') && props.isVisible,
      include: () => [
        ...document.querySelectorAll('.v-navigation-drawer--open'),
        ...document.querySelectorAll('.v-menu__content'),
        ...document.querySelectorAll('.v-dialog__content'),
        ...document.querySelectorAll('.Vue-Toastification__toast'),
      ],
      setCustomerTableTaggings,
      setSelectedCustomerIds,
      addNominationWithCustomer,

      icons: {
        mdiDotsGrid,
        mdiPlus,
        mdiViewGridPlus,
        mdiCashMarker,
        mdiAccountOutline,
        mdiAccountCircleOutline,
        mdiArrowRightBold,
      },
    }
  },
}
</script>
